import { Injectable } from '@angular/core';
import { switchMap, map } from 'rxjs/operators';
import { Fetch } from '@app/models/fetch';
import { AuthState } from '@app/auth/auth.state';

import { WaterStatus, LeakStatus, ActiveLeak } from '@app/models/water-status/water-status.model';
import { Subject, BehaviorSubject, Observable, pipe, forkJoin, of } from 'rxjs';
import { Location } from '../locations/locations.model';
import { UnitService } from '@app/utils/unit.service';
import { QueryUnit } from '../queries/queries.model';
import { DateTime } from 'luxon';

@Injectable({
    providedIn: 'root',
})
export class WaterStatusService {
    private _activeLeak: Subject<ActiveLeak> = new BehaviorSubject<ActiveLeak>(null);
    public readonly activeLeak: Observable<ActiveLeak> = this._activeLeak.asObservable(); // eslint-disable-line
    private _scrollToLeak: Subject<boolean> = new BehaviorSubject<boolean>(null);
    public readonly scrollToLeak: Observable<boolean> = this._scrollToLeak.asObservable(); // eslint-disable-line

    constructor(private fetch: Fetch, private authState: AuthState, private units: UnitService) {}

    getWaterStatusPipe() {
        return pipe(switchMap((loc: Location) => this.getWaterStatus(loc.ws.id, this.units.get().query as QueryUnit)));
    }

    getWaterStatus(deviceID: string, units: string): Observable<any> {
        return this.fetch
            .getAll<WaterStatus>(`//users/${this.authState.userID()}/devices/${deviceID}/query/active`)
            .req.pipe();
    }

    getLeakStatusPipe() {
        return pipe(switchMap((loc: Location) => this.getLeakStatus(loc.ws.id)));
    }

    getLeakStatus(deviceID: string): Observable<any> {
        return this.fetch
            .getAll<ActiveLeak>(`//users/${this.authState.userID()}/devices/${deviceID}/leaks/active`)
            .req.pipe(
                map((res) => {
                    res.data[0].device_id = deviceID;
                    return res;
                })
            );
    }

    getActiveLeak(deviceID: string, leakID: number) {
        this.fetch
            .getAll(`//users/${this.authState.userID()}/devices/${deviceID}/leaks/${leakID}`)
            .model(ActiveLeak)
            .subscribe((data: ActiveLeak) => {
                this._activeLeak.next(data);
            });
    }

    removeLeak() {
        this._activeLeak.next(null);
    }

    buildLeakDisplayData(startStopString, totalGpm, totalGallons, leakDuration, leakEndDate) {
        const leakDisplayData = {
            startStopString: startStopString,
            flowRate: this.parseValueFromString(totalGpm),
            total: this.parseValueFromString(totalGallons),
            duration: leakDuration,
            durationString: leakEndDate === null ? 'This leak has been running for' : 'This leak ran for',
        };
        return leakDisplayData;
    }

    parseValueFromString(input) {
        const flowrate = input.split('.');
        if (flowrate[0] === '0') {
            return '.' + flowrate[1].slice(0, 2);
        } else {
            return flowrate[0] + '.' + flowrate[1].slice(0, 2);
        }
    }

    getStartStopString(leakStart, leakEnd) {
        const start = DateTime.fromISO(leakStart);
        if (leakEnd === null) {
            return `This leak started on ${start.toFormat('EEE MMM dd, yyyy')} at ${start.toFormat(
                'hh:mm a'
            )} and is currently active.`;
        } else {
            const end = DateTime.fromISO(leakEnd);
            return `This leak ended on ${end.toFormat('EEE MMM dd, yyyy')} at ${end.toFormat('hh:mm a')}`;
        }
    }

    getLeakDuration(leakStart, leakEnd) {
        const start = DateTime.fromISO(leakStart);
        let end;
        if (leakEnd === null) {
            end = DateTime.fromJSDate(new Date());
        } else {
            end = DateTime.fromISO(leakEnd);
        }

        const duration = end.diff(start, ['months', 'days', 'hours', 'minutes']).normalize().toObject();
        if (duration.months > 0) {
            duration.months = duration.months.toFixed();
        }
        if (duration.days > 0) {
            duration.days = duration.days.toFixed();
        }
        if (duration.hours > 0) {
            duration.hours = duration.hours.toFixed();
        }
        if (duration.minutes > 0) {
            duration.minutes = duration.minutes.toFixed();
        }
        return duration;
    }

    triggerScrollToLeak() {
        this._scrollToLeak.next(true);
    }
}
